import type { BusinessDoc } from '../shared/api/converters.js';
// Straight from the module, not the feature barrel: the barrel pulls in
// `OnboardingWizardPage`, which imports this file back through
// `ActiveBusinessLayout` — a real cycle that only survives because the bindings
// are hoisted function declarations.
import { needsOnboarding } from '../features/onboarding/onboardingSteps.js';

/** Per-uid last-active business id, for legacy-URL resolution (doc 07 §8.1). */
const keyFor = (uid: string) => `tc24.activeBusiness.${uid}`;

export function readLastActive(uid: string): string | null {
  try {
    return localStorage.getItem(keyFor(uid));
  } catch {
    return null;
  }
}

export function writeLastActive(uid: string, businessId: string): void {
  try {
    localStorage.setItem(keyFor(uid), businessId);
  } catch {
    /* private-mode / disabled storage: last-active is a nicety, not required */
  }
}

/**
 * Which business a bare/legacy URL should land on: the stored last-active if it
 * still exists, else the first non-archived business, else the first business,
 * else null (⇒ send the owner to create their first business).
 */
export function resolveActiveId(businesses: BusinessDoc[], uid: string): string | null {
  const stored = readLastActive(uid);
  if (stored && businesses.some((b) => b.id === stored)) return stored;
  const firstLive = businesses.find((b) => b.status !== 'archived');
  return firstLive?.id ?? businesses[0]?.id ?? null;
}

/**
 * Where `/` sends a signed-in owner (doc 07 §8.1's sibling case).
 *
 * One business is unambiguous, so go straight into it; several means the owner
 * has not said which, so the honest destination is the list they choose from;
 * none means there is nothing to show yet and the only useful screen is the
 * creation wizard.
 *
 * Takes the NON-ARCHIVED businesses. An account whose last live business was
 * archived would otherwise be dropped into an archived one, where every action
 * is disabled — the list, which explains the state, is the right answer there.
 *
 * Deliberately ignores the stored last-active that {@link resolveActiveId}
 * honours: those callers have a section to show and need some business to show
 * it for, while `/` names nothing at all, and silently resuming a business the
 * owner did not ask for is how a two-business account edits the wrong one.
 */
export function homePath(active: BusinessDoc[]): string {
  if (active.length === 0) return '/businesses/new';
  if (active.length === 1) {
    const only = active[0]!;
    // Straight into the wizard rather than to a dashboard the gate would bounce
    // them out of anyway. The redirect would land them here regardless; going
    // directly spares them a visible hop through a screen they may not enter.
    return needsOnboarding(only) ? `/b/${only.id}/setup` : `/b/${only.id}`;
  }
  return '/businesses';
}

/**
 * Where the setup gate sends this request, or `null` to render it as asked.
 *
 * The whole gate as one pure function, so the rule can be tested against every
 * route rather than only observed through a rendered router — and so there is
 * exactly one place that decides it. `ActiveBusinessLayout` is the only caller.
 *
 * Two directions:
 *
 * - **Unfinished setup** admits only the wizard (and the retired `/subscribe`
 *   paths, which redirect into it carrying Stripe's `session_id`). Everything else — dashboard, calls, settings, staff, the test
 *   call — goes back to `/setup`. A half-configured business is not a degraded
 *   product, it is a phone line that does not answer.
 * - **Finished setup** turns the wizard away, because there is nothing left for
 *   it to say. `/subscribe` is exempt in both directions: `SubscribeRedirect`
 *   decides where it goes, and a gate bounce would drop the session id.
 */
export function setupGateRedirect(
  business: Pick<BusinessDoc, 'id' | 'status' | 'onboardingCompletedAt' | 'createdAt'>,
  pathname: string,
): string | null {
  const base = `/b/${business.id}`;
  const onSetup = pathname === `${base}/setup` || pathname.startsWith(`${base}/setup/`);
  const onSubscribe = pathname === `${base}/subscribe` || pathname.startsWith(`${base}/subscribe/`);

  if (needsOnboarding(business as BusinessDoc) && !stuckInProvisioning(business.status)) {
    return onSetup || onSubscribe ? null : `${base}/setup`;
  }
  return onSetup ? base : null;
}

/**
 * Statuses the wizard cannot move a business out of.
 *
 * These belong to the regulated-number path that doc 13a §9 retired: a business
 * sitting at `pending_review` is waiting on a compliance queue, and no step in
 * the wizard advances it. The only screen that can — Settings › Number, which
 * owns the `syncComplianceStatus` retry — is on the far side of the gate.
 *
 * So locking them would be a lockout with no exit: every route bounces to a
 * wizard that cannot help, and the one page that could is unreachable. They are
 * let through instead. The forward-number flow never produces these states, so
 * in practice this covers businesses stranded before that switch — but a gate
 * whose failure mode is "trapped forever" has to say why it cannot happen.
 */
function stuckInProvisioning(status: BusinessDoc['status']): boolean {
  return (
    status === 'collecting_docs' ||
    status === 'pending_review' ||
    status === 'rejected' ||
    status === 'approved' ||
    status === 'provisioning'
  );
}
